import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  AlertTriangle,
  User,
  Building,
  Calendar,
  DollarSign,
  CheckCircle,
  Loader2,
  History,
  XCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const LatePayers = () => {
  const { token, isManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [latePayers, setLatePayers] = useState([]);
  const [clearingFlag, setClearingFlag] = useState(null);
  const [confirmClear, setConfirmClear] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchLatePayers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tenants/late-payers`, { headers });
      setLatePayers(res.data);
    } catch (error) {
      toast.error("Failed to load late payers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLatePayers();
  }, [token]);

  const handleClearFlag = async (tenantId) => {
    setClearingFlag(tenantId);
    try {
      await axios.put(`${API}/tenants/${tenantId}/clear-late-flag`, {}, { headers });
      toast.success("Late payer flag cleared");
      setConfirmClear(null);
      fetchLatePayers();
    } catch (error) {
      toast.error("Failed to clear flag");
    } finally {
      setClearingFlag(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-green-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="late-payers-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Frequent Late Payers</h2>
            <p className="text-sm text-slate-500">
              Tenants with 3+ late payments in the last 12 months
            </p>
          </div>
        </div>
        <Badge className="bg-red-100 text-red-700">
          {latePayers.length} flagged
        </Badge>
      </div>

      {latePayers.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-400" />
            <p className="text-lg font-medium text-slate-700">No Late Payers</p>
            <p className="text-slate-500 mt-1">
              All tenants are paying on time. Great job!
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>Late Payments</TableHead>
                  <TableHead>Current Rent</TableHead>
                  <TableHead>Status</TableHead>
                  {isManager && <TableHead className="text-right">Action</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {latePayers.map((tenant) => (
                  <TableRow key={tenant.id} data-testid={`late-payer-${tenant.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-red-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-red-600" />
                        </div>
                        <div>
                          <p className="font-medium">{tenant.name}</p>
                          {tenant.email && (
                            <p className="text-xs text-slate-500">{tenant.email}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-slate-400" />
                        <div>
                          <p className="text-sm">{tenant.property?.address || "N/A"}</p>
                          {tenant.unit_number && (
                            <p className="text-xs text-slate-500">Unit {tenant.unit_number}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <History className="w-4 h-4 text-amber-500" />
                        <div>
                          <p className="font-bold text-red-600">
                            {tenant.late_payment_count || 0}
                          </p>
                          <p className="text-xs text-slate-500">in 12 months</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {tenant.lease ? (
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">
                            ${tenant.lease.monthly_rent?.toLocaleString() || tenant.lease.rent_amount?.toLocaleString()}/mo
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-400">No active lease</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-red-100 text-red-700">
                        <XCircle className="w-3 h-3 mr-1" />
                        Late Payer
                      </Badge>
                    </TableCell>
                    {isManager && (
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setConfirmClear(tenant)}
                          disabled={clearingFlag === tenant.id}
                          data-testid={`clear-flag-${tenant.id}`}
                        >
                          {clearingFlag === tenant.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-1 text-green-600" />
                              Clear Flag
                            </>
                          )}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Payment History Details */}
      {latePayers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Payment History Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {latePayers.map((tenant) => (
                tenant.payment_history?.length > 0 && (
                  <div key={tenant.id} className="border rounded-lg p-4">
                    <p className="font-medium mb-3">{tenant.name}</p>
                    <div className="space-y-2">
                      {tenant.payment_history.slice(-5).reverse().map((record, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded"
                        >
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            <span>{record.period}</span>
                          </div>
                          {record.days_late > 0 ? (
                            <Badge className="bg-red-100 text-red-700">
                              {record.days_late} days late
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700">
                              On time
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog open={!!confirmClear} onOpenChange={() => setConfirmClear(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear Late Payer Flag?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmClear && (
                <>
                  Remove the late payer flag from <strong>{confirmClear.name}</strong>?
                  <br /><br />
                  This tenant has been late {confirmClear.late_payment_count} times.
                  The flag will be automatically reapplied if they are late 3+ times in 12 months.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmClear && handleClearFlag(confirmClear.id)}
              className="bg-green-700 hover:bg-green-800"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Clear Flag
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LatePayers;
